import React from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Save,
  Share2,
  Settings,
  MoreHorizontal,
  Users,
  Shield,
  Eye,
  GitBranch,
  CheckCircle,
  Clock,
  Loader2,
  AlertCircle,
  BarChart3,
  Workflow,
  Database,
  FileJson,
  Undo,
  Redo,
  Plus,
  X,
  Play,
  Cpu,
  Server,
  Globe,
  Layers,
} from "lucide-react";
import { PermissionGuard } from "@/components/permissions/PermissionGuard";
import { PermissionAction, ResourceType } from "@/types/permissions";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AccessibleNavigation,
  NavigationLandmark,
  MainContentLandmark,
  CommandPalette,
  SpatialNavigationProvider,
} from "@/components/navigation";
import { keyboardShortcuts } from "@/utils/navigation";
import { Footer } from "./Footer";
import { KnowledgeModal } from "@/components/knowledge/KnowledgeModal";
import { useEditorStore, useEditorHistory } from "@/stores/editor/editor-store";
import { useRightPanelStore } from "@/stores/editor/right-panel-store";
import { AnalyticsConfigurationModal } from "@/components/analytics/AnalyticsConfigurationModal";
import { ViewportSelector } from "@/components/editor/ViewportSelector";
import { ZoomControls } from "@/components/editor/ZoomControls";
import { GenerationProgressModal } from "@/components/editor/GenerationProgressModal";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { OrganizationContextSwitcher } from "@/components/organization/OrganizationContextSwitcher";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";
import { useOrganizationStore } from "@/stores/organization/organization-store";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { ComponentType } from "@/types";

interface EditorLayoutProps {
  children: React.ReactNode;
  onOpenPermissionMatrix?: () => void;
  onOpenProjectSettings?: () => void;
  onOpenSharing?: () => void;
  onToggleVersioning?: () => void;
  isVersioningOpen?: boolean;
  isVersioningMode?: boolean;
}

export const EditorLayout: React.FC<EditorLayoutProps> = ({
  children,
  onOpenPermissionMatrix,
  onOpenProjectSettings,
  onOpenSharing,
  onToggleVersioning,
  isVersioningOpen,
  isVersioningMode,
}) => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();

  // Get save state and sketch state from editor store
  const {
    isModified,
    lastSaved,
    saveProject,
    sketches,
    isGenerating,
    generationStage,
    generationError,
    resetGenerationProgress,
  } = useEditorStore();

  const { canUndo, canRedo, undo, redo } = useEditorHistory();
  const rightPanelMode = useRightPanelStore((state) => state.mode);
  const toggleAdd = useRightPanelStore((state) => state.toggleAdd);
  const toggleLayers = useRightPanelStore((state) => state.toggleLayers);
  const editorMode = useEditorStore((state) => state.editorMode);
  const workflowId = useEditorStore((state) => state.workflowId);
  const workflowName = useEditorStore((state) => state.workflowName);
  const workflowActions = useEditorStore((state) => state.workflowActions);
  const enterWorkflowMode = useEditorStore((state) => state.enterWorkflowMode);
  const exitWorkflowMode = useEditorStore((state) => state.exitWorkflowMode);
  const dataModeName = useEditorStore((state) => state.dataModeName);
  const dataActions = useEditorStore((state) => state.dataActions);
  const enterDataMode = useEditorStore((state) => state.enterDataMode);
  const exitDataMode = useEditorStore((state) => state.exitDataMode);
  const currentPageId = useEditorStore((state) => state.currentPageId);
  const platformAdminEntitlement = useOrganizationStore(
    (state) => state.platformAdminEntitlement,
  );

  const [isSaving, setIsSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [isAnalyticsModalOpen, setIsAnalyticsModalOpen] = React.useState(false);
  const projectResource = projectId
    ? { type: ResourceType.PROJECT, id: projectId }
    : null;

  // Get selected component to check for form with workflow
  // Note: Avoid creating new array references in selectors (e.g. ?? []) as it causes infinite loops
  const selectedComponents = useEditorStore((state) => state.selectedComponents);
  const components = useEditorStore((state) => state.components);

  // Check if selected component is a form with a workflow
  const selectedFormWorkflowId = React.useMemo(() => {
    // Safely handle null/undefined arrays
    const ids = selectedComponents ?? [];
    const comps = components ?? [];
    if (ids.length !== 1) return null;
    if (comps.length === 0) return null;
    const selectedComponent = comps.find((c) => c.id === ids[0]);
    const workflowId = selectedComponent?.properties?.workflowId;
    if (
      selectedComponent?.type === ComponentType.FORM_CONTAINER &&
      typeof workflowId === "string"
    ) {
      return workflowId;
    }
    return null;
  }, [selectedComponents, components]);

  // Helper to format last saved time
  const formatSaveTime = (date: Date): string => {
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) {
      return "just now";
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes}m ago`;
    } else {
      return date.toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      });
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setSaveError(null);
      await saveProject();
    } catch (error) {
      setSaveError("Save failed");
    } finally {
      setIsSaving(false);
    }
  };

  // Register editor-specific keyboard shortcuts
  React.useEffect(() => {
    const shortcuts = [
      {
        id: "editor-save",
        keys: ["meta", "s"],
        description: "Save project",
        category: "Editor",
        action: () => {
          const event = new CustomEvent("editor-save");
          window.dispatchEvent(event);
        },
      },
      {
        id: "editor-fullscreen",
        keys: ["f11"],
        description: "Toggle fullscreen",
        category: "Editor",
        action: () => {
          const event = new CustomEvent("toggle-fullscreen");
          window.dispatchEvent(event);
        },
      },
      {
        id: "editor-zen-mode",
        keys: ["meta", "shift", "z"],
        description: "Toggle zen mode",
        category: "Editor",
        action: () => {
          const event = new CustomEvent("toggle-zen-mode");
          window.dispatchEvent(event);
        },
      },
    ];

    shortcuts.forEach((shortcut) => keyboardShortcuts.register(shortcut));

    return () => {
      shortcuts.forEach((shortcut) =>
        keyboardShortcuts.unregister(shortcut.keys),
      );
    };
  }, []);

  return (
    <SpatialNavigationProvider
      config={{
        enabled: true,
        focusRingVisible: true,
        rememberLastFocus: true,
      }}
    >
      <AccessibleNavigation skipToContentId="editor-canvas">
        <div className="min-h-screen bg-background flex flex-col">
          {/* Editor Header */}
          <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40">
            <NavigationLandmark label="Editor navigation">
              <div className="px-4 py-2">
                <div className="flex items-center justify-between">
                  {/* Left Section - Back to Projects + Toolbar */}
                  <div className="flex items-center gap-2">
                    <Link
                      to="/projects"
                      className="flex items-center gap-2 px-2 py-1 hover:bg-accent rounded-md transition-colors"
                      aria-label="Back to projects"
                    >
                      <ArrowLeft className="h-4 w-4" />
                      <span className="text-sm">Projects</span>
                    </Link>

                    {editorMode === 'visualization' && (
                      <>
                        <div className="w-px h-6 bg-border" />

                        {/* Viewport & Zoom */}
                        <ViewportSelector showDimensions={true} />
                        <ZoomControls showPercentage={true} size="sm" />

                        <div className="w-px h-6 bg-border" />

                        {/* Add panel toggle — swaps the right panel between AI chat and the add (components/templates) panel */}
                        <Button
                          variant={rightPanelMode === 'add' ? 'default' : 'ghost'}
                          size="sm"
                          className="h-8 gap-1.5 text-xs"
                          onClick={toggleAdd}
                          aria-pressed={rightPanelMode === 'add'}
                          title={rightPanelMode === 'add' ? 'Show AI chat' : 'Add components or templates'}
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Add
                        </Button>

                        <Button
                          variant={rightPanelMode === 'layers' ? 'default' : 'ghost'}
                          size="sm"
                          className="h-8 gap-1.5 text-xs"
                          onClick={toggleLayers}
                          aria-pressed={rightPanelMode === 'layers'}
                          title={rightPanelMode === 'layers' ? 'Show AI chat' : 'Show layer tree'}
                        >
                          <Layers className="w-3.5 h-3.5" />
                          Layers
                        </Button>

                        <div className="w-px h-6 bg-border" />
                      </>
                    )}

                    {/* Undo/Redo */}
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={undo} disabled={!canUndo} aria-label="Undo">
                        <Undo className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={redo} disabled={!canRedo} aria-label="Redo">
                        <Redo className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Workflow Mode: Target Selector */}
                    {editorMode === 'workflow' && workflowActions && (
                      <>
                        <div className="w-px h-6 bg-border" />
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Target:</span>
                          <Select
                            value={workflowActions.selectedTarget}
                            onValueChange={(value) => workflowActions.setSelectedTarget(value)}
                          >
                            <SelectTrigger className="w-[120px] h-7 text-xs">
                              <SelectValue>
                                <div className="flex items-center gap-1.5">
                                  {workflowActions.selectedTarget === 'esp32' ? (
                                    <Cpu className="w-3.5 h-3.5" />
                                  ) : (
                                    <Server className="w-3.5 h-3.5" />
                                  )}
                                  {workflowActions.selectedTarget === 'esp32' ? 'ESP32' : 'Server'}
                                </div>
                              </SelectValue>
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="server">
                                <div className="flex items-center gap-2">
                                  <Server className="w-4 h-4" />
                                  Server
                                </div>
                              </SelectItem>
                              <SelectItem value="esp32">
                                <div className="flex items-center gap-2">
                                  <Cpu className="w-4 h-4" />
                                  ESP32
                                </div>
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          variant="default"
                          size="sm"
                          className="h-8 text-xs gap-1.5"
                          onClick={() => workflowActions.handleExecute()}
                        >
                          <Play className="w-3.5 h-3.5" />
                          Run
                        </Button>
                        <div className="w-px h-6 bg-border" />
                        <div className="flex items-center gap-1.5">
                          <Switch
                            id="publish-switch-embedded"
                            checked={workflowActions.workflowStatus === 'published'}
                            onCheckedChange={(checked) => workflowActions.handlePublishToggle(checked)}
                            disabled={workflowActions.isSaving}
                            className="scale-90"
                          />
                          <Label
                            htmlFor="publish-switch-embedded"
                            className={cn(
                              "text-xs cursor-pointer select-none",
                              workflowActions.workflowStatus === 'published'
                                ? 'text-green-600 font-medium'
                                : 'text-muted-foreground'
                            )}
                          >
                            {workflowActions.workflowStatus === 'published' ? 'Published' : 'Draft'}
                          </Label>
                        </div>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-8 w-8 p-0"
                                onClick={() => workflowActions.openEnvironmentsDialog()}
                              >
                                <Globe className="w-3.5 h-3.5" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent>Run Environments</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </>
                    )}

                    {/* Data Mode: Sources button */}
                    {editorMode === 'data' && dataActions && (
                      <>
                        <div className="w-px h-6 bg-border" />
                        <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs"
                          onClick={() => dataActions.setActiveTab('sources')}>
                          <Database className="w-3.5 h-3.5" />
                          Sources
                        </Button>
                      </>
                    )}

                  </div>

                  {/* Center Section - Workflow Mode Indicator / Form Workflow Button */}
                  <div className="hidden lg:flex flex-1 justify-center items-center gap-3 px-4">
                    {editorMode === 'workflow' ? (
                      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/30">
                        <Workflow className="h-3.5 w-3.5 text-primary" />
                        <span className="text-sm font-medium text-primary truncate max-w-[200px]">
                          {workflowName || 'Workflow'}
                        </span>
                        <button
                          onClick={exitWorkflowMode}
                          className="ml-1 p-0.5 rounded-full hover:bg-primary/10 text-primary/60 hover:text-primary transition-colors"
                          aria-label="Exit workflow mode"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : editorMode === 'data' ? (
                      <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-primary/5 border border-primary/30">
                        <Database className="h-3.5 w-3.5 text-primary" />
                        <span className="text-sm font-medium text-primary truncate max-w-[200px]">
                          {dataModeName || 'Data'}
                        </span>
                        <button
                          onClick={exitDataMode}
                          className="ml-1 p-0.5 rounded-full hover:bg-primary/10 text-primary/60 hover:text-primary transition-colors"
                          aria-label="Exit data mode"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : selectedFormWorkflowId ? (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 gap-1.5 text-xs border-primary/30 bg-primary/5 hover:bg-primary/10"
                              onClick={() => enterWorkflowMode(selectedFormWorkflowId)}
                            >
                              <Workflow className="h-3.5 w-3.5" />
                              <span>Form Workflow</span>
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            Open the workflow connected to this form
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    ) : null}
                  </div>

                  {/* Right Section - Actions */}
                  <div className="flex items-center gap-2">
                    <OrganizationContextSwitcher />
                    <NotificationCenter />

                    {/* Save Status Indicator */}
                    {(() => {
                      const isWorkflowMode = editorMode === 'workflow' && workflowActions;
                      const isDataMode = editorMode === 'data';
                      const effectiveSave = isWorkflowMode
                        ? () => workflowActions.handleSave()
                        : handleSave;
                      const effectiveIsSaving = isWorkflowMode
                        ? workflowActions.isSaving
                        : isSaving;
                      const effectiveError = isWorkflowMode
                        ? workflowActions.error
                        : isDataMode ? null : saveError;
                      // In workflow mode, always allow save; in data mode, disable save
                      const effectiveDisabled = isWorkflowMode
                        ? effectiveIsSaving
                        : isDataMode
                          ? true
                          : effectiveIsSaving || (!isModified && !saveError);

                      return (
                    <button
                      onClick={effectiveSave}
                      disabled={effectiveDisabled}
                      className={cn(
                        "flex items-center gap-2 px-3 h-8 text-sm rounded-md transition-colors border",
                        effectiveError
                          ? "bg-destructive/10 border-destructive/30"
                          : !isWorkflowMode && !isDataMode && lastSaved && !isModified
                            ? "bg-emerald-500/10 border-emerald-500/30"
                            : "border-border bg-transparent",
                        effectiveDisabled && "cursor-not-allowed",
                      )}
                      aria-label={effectiveIsSaving ? "Saving..." : isWorkflowMode ? "Save workflow" : isModified ? "Save project" : "Project saved"}
                    >
                      {effectiveIsSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <span>Saving...</span>
                        </>
                      ) : effectiveError ? (
                        <>
                          <AlertCircle className="h-4 w-4 text-destructive" />
                          <span className="text-destructive">Save Failed</span>
                        </>
                      ) : !isWorkflowMode && !isDataMode && lastSaved && !isModified ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-emerald-500" />
                          <span className="text-emerald-500">Saved</span>
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatSaveTime(lastSaved)}
                          </span>
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4" />
                          <span>Save</span>
                        </>
                      )}
                    </button>
                      );
                    })()}

                    {/* Permission-aware Action Buttons */}
                    <div className="flex items-center border border-border rounded-md h-8">
                      <TooltipProvider>
                        {/* View Mode Toggle Button */}
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              onClick={() => navigate(`/viewer/${projectId}${currentPageId ? `/page/${currentPageId}` : ''}`)}
                              className="h-8 w-8 flex items-center justify-center hover:bg-accent transition-colors"
                              aria-label="Switch to view mode"
                              title="View Mode"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>Switch to view mode</TooltipContent>
                        </Tooltip>

                        <div className="h-4 w-px bg-border" />

                        {/* More Options Dropdown */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              className="h-8 w-8 flex items-center justify-center hover:bg-accent rounded-r-md transition-colors"
                              aria-label="More options"
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end" className="w-48">
                            {/* Share Item - disabled */}
                            <DropdownMenuItem disabled>
                              <Share2 className="h-4 w-4 mr-2" />
                              Share
                            </DropdownMenuItem>

                            {/* Team Members Item */}
                            {projectResource && (
                              <PermissionGuard
                                resource={projectResource}
                                action={PermissionAction.MANAGE_MEMBERS}
                                fallback={
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <DropdownMenuItem
                                        disabled
                                        className="opacity-50"
                                      >
                                        <Users className="h-4 w-4 mr-2" />
                                        Team
                                      </DropdownMenuItem>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                      You don't have permission to manage team
                                    </TooltipContent>
                                  </Tooltip>
                                }
                              >
                                <DropdownMenuItem>
                                  <Users className="h-4 w-4 mr-2" />
                                  Team
                                </DropdownMenuItem>
                              </PermissionGuard>
                            )}

                            {/* Permissions Item - disabled */}
                            <DropdownMenuItem disabled>
                              <Shield className="h-4 w-4 mr-2" />
                              Permissions
                            </DropdownMenuItem>

                            {/* Project Settings Item */}
                            {projectResource && (
                              <PermissionGuard
                                resource={projectResource}
                                action={PermissionAction.MANAGE_SETTINGS}
                                fallback={
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <DropdownMenuItem
                                        disabled
                                        className="opacity-50"
                                      >
                                        <Settings className="h-4 w-4 mr-2" />
                                        Settings
                                      </DropdownMenuItem>
                                    </TooltipTrigger>
                                    <TooltipContent side="left">
                                      You don't have permission to modify settings
                                    </TooltipContent>
                                  </Tooltip>
                                }
                              >
                                <DropdownMenuItem onClick={onOpenProjectSettings}>
                                  <Settings className="h-4 w-4 mr-2" />
                                  Settings
                                </DropdownMenuItem>
                              </PermissionGuard>
                            )}

                            {/* Analytics Item - disabled */}
                            <DropdownMenuItem disabled>
                              <BarChart3 className="h-4 w-4 mr-2" />
                              Analytics
                            </DropdownMenuItem>

                            {/* Workflow Item */}
                            <DropdownMenuItem onClick={() => {
                              const lastId = projectId && localStorage.getItem(`lastWorkflow:${projectId}`);
                              enterWorkflowMode(lastId || '');
                            }}>
                              <Workflow className="h-4 w-4 mr-2" />
                              Workflow
                            </DropdownMenuItem>

                            {/* Data Item */}
                            <DropdownMenuItem onClick={() => enterDataMode()}>
                              <Database className="h-4 w-4 mr-2" />
                              Data
                            </DropdownMenuItem>

                            {/* Devices Item */}
                            <DropdownMenuItem onClick={() => navigate("/devices")}>
                              <Cpu className="h-4 w-4 mr-2" />
                              Devices
                            </DropdownMenuItem>

                            {platformAdminEntitlement && (
                              <DropdownMenuItem
                                onClick={() => navigate("/viewer/template")}
                              >
                                <FileJson className="h-4 w-4 mr-2" />
                                Templates
                              </DropdownMenuItem>
                            )}

                            <DropdownMenuSeparator />

                            {/* Version History */}
                            <DropdownMenuItem onClick={onToggleVersioning}>
                              <GitBranch className="h-4 w-4 mr-2" />
                              Versions
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TooltipProvider>
                    </div>
                  </div>
                </div>

              </div>
            </NavigationLandmark>
          </header>

          {/* Editor Content */}
          <div
            className="flex flex-col overflow-hidden"
            style={{ height: 'calc(100vh - var(--header-height))' }}
          >
            <MainContentLandmark id="editor-canvas">
              {children}
            </MainContentLandmark>
          </div>

          {/* Footer */}
          <Footer />

          {/* Command Palette */}
          <CommandPalette />

          {/* Knowledge Modal (global, shared with RootLayout) */}
          <KnowledgeModal />

          {/* Analytics Configuration Modal */}
          <AnalyticsConfigurationModal
            isOpen={isAnalyticsModalOpen}
            onClose={() => setIsAnalyticsModalOpen(false)}
            projectId={projectId}
          />

          {/* Generation Progress Modal */}
          <GenerationProgressModal
            isOpen={isGenerating || generationStage === 'complete' || generationStage === 'error'}
            currentStage={generationStage || 'preparing'}
            sketchCount={sketches.length}
            error={generationError || undefined}
            onClose={resetGenerationProgress}
          />

          {/* WorkflowListModal removed — workflow selection now via WorkflowSelector in canvas header */}
        </div>
      </AccessibleNavigation>
    </SpatialNavigationProvider>
  );
};
